/**
 * TanStack Query mutation functions for booking-related operations
 */

import { buildApiUrl } from "../config/urlConfig.js";
import { invalidateQueriesForOperation } from "../../services/cacheStrategy.js";
import { getValidationErrorMessage } from "../../services/validationMessages.js";

const checkServerResponse = (response, data, bookingData, endpoint) => {
	// Check for HTTP errors
	if (!response.ok) {
		const error = data.error || 'Unknown error';
		throw new Error(`Server error: ${error} (HTTP ${response.status})`);
	}

	// Check for validation errors first
	if (data.error && data.invalid) {
		const validationError = new Error(data.error);
		validationError.isValidation = true;
		validationError.validationMessages = data.messages || {};
		throw validationError;
	}

	// Check for budget errors
	if (data.error && data.budget) {
		const budgetError = new Error(data.error);
		budgetError.status = response.status;
		budgetError.budget = data.budget;
		budgetError.budgetDetails = data.budgetDetails || [];
		budgetError.originalData = bookingData;
		budgetError.endpoint = endpoint;
		throw budgetError;
	}

	// Check for booking conflict or closed building errors
	if (data.error && (data.conflict || data.closed)) {
		const conflictError = new Error(data.error);
		conflictError.isConflict = true;
		conflictError.conflictingDates = data.conflictingDates || [];
		conflictError.closedDates = data.closedDates || [];
		conflictError.hasBookingConflicts = data.conflict || false;
		conflictError.hasClosedHours = data.closed || false;
		conflictError.originalData = bookingData;
		conflictError.endpoint = endpoint;
		throw conflictError;
	}

	// Check for compliance errors
	if (data.error && (data.coolDownViolation || data.maxBookingViolation || data.suspiciousPattern)) {
		const complianceError = new Error(data.error);
		complianceError.isCompliance = true;
		complianceError.coolDownViolation = data.coolDownViolation || false;
		complianceError.coolDownInSeconds = data.coolDownInSeconds || null;
		complianceError.maxBookingViolation = data.maxBookingViolation || false;
		complianceError.maxBookingPerDay = data.maxBookingPerDay || null;
		complianceError.suspiciousPattern = data.suspiciousPattern || false;
		complianceError.originalData = bookingData;
		complianceError.endpoint = endpoint;
		throw complianceError;
	}
}

/**
 * Create a new booking
 * @param {Object} bookingData - Booking data
 * @param {number} roomId - Room ID
 * @param {string} endpoint - API endpoint ('instance/create' or 'series/create')
 * @returns {Promise<Object>} Created booking result
 */
export const createBooking = async ({
	bookingData,
	roomId,
	endpoint = "instance/create",
}) => {
	// Remove fields that shouldn't be sent to API
	const { 
		roomId: _, 
		selectedPersonName, 
		selectedPersonIdForDisplay, 
		bookWithoutFunds,
		personType,
		...cleanBookingData 
	} = bookingData;

	const url = buildApiUrl("roomBooking", `/${endpoint}`);
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({
			...cleanBookingData,
			roomId,
		}),
	});

	const data = await response.json();
	checkServerResponse(response, data, bookingData, endpoint);

	if (data.success && (data.booking || data.series)) {
		if (data.series && data.series.occurrences) {
			return {
				type: "series",
				data: data.series,
				bookings: data.series.occurrences,
			};
		} else if (data.booking) {
			return {
				type: "booking",
				data: data.booking,
				bookings: [data.booking],
			};
		}
	}

	throw new Error("Invalid response from server");
};

/**
 * Create a walk-in (ad-hoc) booking
 * @param {Object} params - Parameters
 * @param {number} params.roomId - Room ID
 * @param {number} params.durationMinutes - Duration in minutes (30-120, 15-minute steps)
 * @param {?string} [params.title] - Optional booking title
 * @param {?string} [params.description] - Optional booking description
 * @returns {Promise<Object>} Created booking result
 */
export const createWalkInBooking = async ({
	roomId,
	durationMinutes,
	title = null,
	description = null,
}) => {
	const url = buildApiUrl("roomBooking", "/walk-in/create");
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({ roomId, durationMinutes, title, description }),
	});

	const data = await response.json();

	// Walk-in has none of the budget/compliance/conflict-detail error shapes the regular
	// create flow needs - just a plain error string on failure. Only validation errors
	// need translating: they report message keys instead of user-facing wording.
	if (!response.ok || !data.success) {
		if (data.invalid) {
			throw new Error(getValidationErrorMessage(data.messages));
		}

		throw new Error(data.error || "Unbekannter Fehler");
	}

	return {
		type: "booking",
		data: data.booking,
		bookings: [data.booking],
	};
};

/**
 * Update an existing booking
 * @param {Object} params - Parameters
 * @param {number} params.bookingId - Booking ID
 * @param {Object} params.bookingData - Updated booking data
 * @param {number} params.roomId - Room ID
 * @returns {Promise<Object>} Updated booking
 */
export const updateBooking = async ({ bookingId, bookingData, roomId }) => {
	// Remove fields that shouldn't be sent to API
	const { 
		roomId: _, 
		selectedPersonName, 
		selectedPersonIdForDisplay, 
		bookWithoutFunds,
		personType,
		...cleanBookingData 
	} = bookingData;

	const endpoint = "instance/update";
	const url = buildApiUrl("roomBooking", `/${endpoint}`);
	const response = await fetch(url, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({
			...cleanBookingData,
			id: bookingId,
			roomId,
		}),
	});

	const data = await response.json();
	checkServerResponse(response, data, bookingData, endpoint);

	if (data.success && data.booking) {
		return data.booking;
	}

	throw new Error("Invalid response from server");
};

/**
 * End a currently-running walk-in booking early (ad-hoc end of an ad-hoc booking)
 * @param {Object} params - Parameters
 * @param {number} params.bookingId - Booking ID
 * @returns {Promise<Object>} Updated (shortened) booking
 */
export const endWalkInBooking = async ({ bookingId }) => {
	const url = buildApiUrl("roomBooking", "/walk-in/end");
	const response = await fetch(url, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({ id: bookingId }),
	});

	const data = await response.json();

	// Same minimal error shape as walk-in creation - just a plain error string on failure.
	if (!response.ok || !data.success) {
		throw new Error(data.error || "Unbekannter Fehler");
	}

	return data.booking;
};

/**
 * Update a booking series
 * @param {Object} params - Parameters
 * @param {number} params.seriesId - Series ID
 * @param {Object} params.bookingData - Updated booking data
 * @param {number} params.roomId - Room ID
 * @returns {Promise<Object>} Updated series result
 */
export const updateSeries = async ({ seriesId, bookingData, roomId }) => {
	// Remove fields that shouldn't be sent to API
	const { 
		roomId: _, 
		selectedPersonName, 
		selectedPersonIdForDisplay, 
		bookWithoutFunds,
		personType,
		...cleanBookingData 
	} = bookingData;

	const endpoint = "series/update";
	const url = buildApiUrl("roomBooking", `/${endpoint}`);
	const response = await fetch(url, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({
			...cleanBookingData,
			id: seriesId,
			roomId: roomId,
		}),
	});

	const data = await response.json();
	checkServerResponse(response, data, bookingData, endpoint);

	if (data.success && data.series) {
		return {
			series: data.series,
			bookings: data.series.occurrences || [],
		};
	}

	throw new Error("Invalid response from server");
};

/**
 * Delete a booking
 * @param {number} bookingId - Booking ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteBooking = async (bookingId) => {
	const url = buildApiUrl("roomBooking", "/instance/cancel");
	const response = await fetch(url, {
		method: "DELETE",
		headers: {
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({ id: bookingId }),
	});

	const data = await response.json();
	checkServerResponse(response, data, null, null);

	if (data.success) {
		return true;
	}

	throw new Error("Failed to delete booking");
};

/**
 * Delete a booking series
 * @param {number} seriesId - Series ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteSeries = async (seriesId) => {
	const url = buildApiUrl("roomBooking", "/series/cancel");
	const response = await fetch(url, {
		method: "DELETE",
		headers: {
			"X-Requested-With": "XMLHttpRequest",
		},
		body: JSON.stringify({ id: seriesId }),
	});

	const data = await response.json();
	checkServerResponse(response, data, null, null);

	if (data.success) {
		return true;
	}

	throw new Error("Failed to delete series");
};

/**
 * TanStack Query mutation configuration for creating bookings
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useCreateBookingMutation = (queryClient) => {
	return {
		mutationFn: createBooking,
		onSuccess: (data, variables) => {
			// Use centralized cache strategy for precise invalidations
			invalidateQueriesForOperation(queryClient, 'create', {
				roomId: variables.roomId,
				endpoint: variables.endpoint,
				data: variables.bookingData
			});
		},
		onError: (error, variables) => {
			console.error("Failed to create booking:", error);
		},
	};
};

/**
 * TanStack Query mutation configuration for creating walk-in bookings
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useCreateWalkInBookingMutation = (queryClient) => {
	return {
		mutationFn: createWalkInBooking,
		onSuccess: (data, variables) => {
			// Reuse the regular 'create' invalidation strategy - same targets apply
			// (room bookings, all bookings, my bookings), and its series-specific check
			// simply never matches for walk-in.
			invalidateQueriesForOperation(queryClient, "create", {
				roomId: variables.roomId,
			});
		},
		onError: (error) => {
			console.error("Failed to create walk-in booking:", error);
		},
	};
};

/**
 * TanStack Query mutation configuration for updating bookings
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useUpdateBookingMutation = (queryClient) => {
	return {
		mutationFn: updateBooking,
		onSuccess: (data, variables) => {
			// Use centralized cache strategy for precise invalidations
			invalidateQueriesForOperation(queryClient, 'update', {
				roomId: variables.roomId,
				bookingData: variables.bookingData,
				bookingId: variables.bookingId,
				// only set for a single-occurrence edit within a series (see bookingManager.js)
				seriesId: variables.seriesId
			});
		},
		onError: (error, variables) => {
			console.error("Failed to update booking:", error);
		},
	};
};

/**
 * TanStack Query mutation configuration for ending a walk-in booking early
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useEndWalkInBookingMutation = (queryClient) => {
	return {
		mutationFn: endWalkInBooking,
		onSuccess: (data, variables) => {
			// roomId isn't in the mutation variables (only bookingId is) - it's on the
			// returned (shortened) booking instead.
			invalidateQueriesForOperation(queryClient, "update", {
				roomId: data?.roomId,
				bookingId: variables.bookingId,
			});
		},
		onError: (error) => {
			console.error("Failed to end walk-in booking:", error);
		},
	};
};

/**
 * TanStack Query mutation configuration for updating series
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useUpdateSeriesMutation = (queryClient) => {
	return {
		mutationFn: updateSeries,
		onSuccess: (data, variables) => {
			// Use centralized cache strategy for precise invalidations
			invalidateQueriesForOperation(queryClient, 'updateSeries', {
				roomId: variables.roomId,
				seriesId: variables.seriesId,
				bookingData: variables.bookingData
			});
		},
		onError: (error, variables) => {
			console.error("Failed to update series:", error);
		},
	};
};

/**
 * TanStack Query mutation configuration for deleting bookings
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useDeleteBookingMutation = (queryClient) => {
	return {
		mutationFn: deleteBooking,
		onSuccess: (data, variables, context) => {
			// Use centralized cache strategy for precise invalidations
			// Note: roomId might be available in context if passed from bookingManager
			invalidateQueriesForOperation(queryClient, 'delete', {
				bookingId: variables,
				roomId: context?.roomId, // May be undefined, strategy handles this
				seriesId: context?.seriesId
			});
		},
		onError: (error, variables) => {
			console.error("Failed to delete booking:", error);
		},
	};
};

/**
 * TanStack Query mutation configuration for deleting series
 * @param {Object} queryClient - Query client instance for invalidation
 * @returns {Object} Mutation configuration
 */
export const useDeleteSeriesMutation = (queryClient) => {
	return {
		mutationFn: deleteSeries,
		onSuccess: (data, variables, context) => {
			// Use centralized cache strategy for precise invalidations
			invalidateQueriesForOperation(queryClient, 'deleteSeries', {
				seriesId: variables,
				roomId: context?.roomId // May be undefined, strategy handles this
			});
		},
		onError: (error, variables) => {
			console.error("Failed to delete series:", error);
		},
	};
};
